/**
 * The route's cross-section, drawn.
 *
 * Every coordinate in here came out of `profileModel()`; this file decides only
 * which token paints what. Two mechanics are worth knowing:
 *
 * **The SVG is stretched, the text is not.** The box is 1 000 × 280 abstract
 * units with `preserveAspectRatio="none"`, so it fills whatever width the
 * inspector has — 340 px on an iPad, 1 100 px on a wide screen — while
 * `vector-effect: non-scaling-stroke` keeps every line one pixel wide and every
 * dash the same length. Text would be stretched by the same transform, so there
 * is none inside the SVG: the axis labels and the waypoint idents are HTML,
 * positioned in per cent over the same box.
 *
 * **An obstacle tick is one pixel wide and a finger is not.** Each tick carries
 * an invisible 12-unit-wide hit rectangle with the `<title>` on it, so the name
 * appears on hover on a desktop and the tick is reachable on a touch screen.
 */

import { useT, type T } from '../../i18n';
import type { ProfileModel, ProfileObstacle } from './profile';

/** The obstacle kinds openAIP publishes that have a word of their own. */
const OBSTACLE_KINDS: readonly string[] = ['chimney', 'building', 'wind_turbine', 'tower'];

/** „Mast ≥ 1119 ft, Abschnitt 3" — the ≥ is `exact: false` and is not decoration. */
export function obstacleTitle(t: T, o: ProfileObstacle): string {
  const name = o.name || (OBSTACLE_KINDS.includes(o.kind) ? t('obst.kind.' + o.kind) : '');
  const key = name ? 'terrain.obst' : 'terrain.obst.plain';
  return t(o.exact ? key : key + '.min', { name, ft: o.ft }) + ' · ' + t('terrain.as.leg', { n: o.leg });
}

const pct = (v: number, of: number): string => (v / of) * 100 + '%';

export function ProfileChart({
  model,
  stale = false,
  labels,
}: {
  model: ProfileModel;
  /** a newer profile is on its way; keep the drawing and dim it */
  stale?: boolean;
  /** what to write under each waypoint tick — ident, and ETO where there is one */
  labels: { ident: string; eto: string }[];
}) {
  const t = useT();
  const { w, h } = model;

  return (
    <div className={'aet-chart' + (stale ? ' stale' : '')}>
      <div className="aet-plot">
        <svg
          className="aet-svg"
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={t('terrain.chart.aria', { nm: model.totalNm, ft: model.topFt })}
        >
          {/* the gridlines first: everything else is read against them */}
          {model.grid.map(g => (
            <line key={'g' + g.ft} x1={0} y1={g.y} x2={w} y2={g.y} className="aet-grid" vectorEffect="non-scaling-stroke" />
          ))}

          {/* the gap between the planned altitude and the highest thing under it */}
          {model.legs.map(l =>
            l.band && l.band.h > 0 ? (
              <rect
                key={'b' + l.leg}
                x={l.band.x}
                y={l.band.y}
                width={l.band.w}
                height={l.band.h}
                className={'aet-band aet-' + l.level}
              />
            ) : null,
          )}

          {/* Obstacles stand ON the ground, so their stems go UNDER the fill:
              what is buried is hidden and only the part that clears the terrain
              is drawn. The hit areas come last, over everything, or a one-pixel
              tick would be unhoverable and untappable. */}
          {model.obstacles.map(o => (
            <g key={o.key} className={'aet-obst' + (o.conflict ? ' conflict' : '') + (o.exact ? '' : ' approx')}>
              <line x1={o.x} y1={o.y} x2={o.x} y2={h} className="aet-obst-stem" vectorEffect="non-scaling-stroke" />
              <line x1={o.x - 5} y1={o.y} x2={o.x + 5} y2={o.y} className="aet-obst-cap" vectorEffect="non-scaling-stroke" />
            </g>
          ))}

          {/* the ground */}
          {model.areas.map((d, i) => (
            <path key={'a' + i} d={d} className="aet-fill" />
          ))}
          {model.lines.map((pts, i) => (
            <polyline key={'l' + i} points={pts} className="aet-line" vectorEffect="non-scaling-stroke" />
          ))}

          {/* the suggested Sicherheitshöhe, then the planned altitude over it */}
          {model.suggested.map((pts, i) => (
            <polyline key={'s' + i} points={pts} className="aet-safe" vectorEffect="non-scaling-stroke" />
          ))}
          {model.planned.map((pts, i) => (
            <polyline key={'p' + i} points={pts} className="aet-planned" vectorEffect="non-scaling-stroke" />
          ))}

          {/* leg boundaries */}
          {model.marks.slice(1, -1).map(m => (
            <line key={'m' + m.index} x1={m.x} y1={0} x2={m.x} y2={h} className="aet-legline" vectorEffect="non-scaling-stroke" />
          ))}

          {model.firstConflict ? (
            <circle
              cx={model.firstConflict.x}
              cy={model.firstConflict.y}
              r={4}
              className="aet-strike"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}

          {model.obstacles.map(o => (
            <rect key={'h' + o.key} x={o.x - 6} y={0} width={12} height={h} className="aet-hit">
              <title>{obstacleTitle(t, o)}</title>
            </rect>
          ))}
        </svg>

        {/* the vertical scale, as HTML so it is not stretched with the box */}
        <div className="aet-yaxis" aria-hidden="true">
          {model.grid.map(g => (
            <span key={'y' + g.ft} className="aet-ytick ae-num" style={{ top: pct(g.y, h) }}>
              {g.ft.toLocaleString('de-DE').replace(/\./g, ' ')}
            </span>
          ))}
        </div>
      </div>

      <div className="aet-xaxis">
        {model.marks.map((m, i) => {
          const label = labels[i];
          if (!label) return null;
          const last = i === model.marks.length - 1;
          return (
            <span
              key={'x' + m.index}
              className={'aet-xtick' + (i === 0 ? ' first' : last ? ' last' : '')}
              style={{ left: pct(m.x, w) }}
            >
              <b>{label.ident}</b>
              {label.eto ? <i className="ae-num">{label.eto}</i> : null}
            </span>
          );
        })}
      </div>
    </div>
  );
}
